import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  PaginatedSearchDropdown,
  Popover,
  Svg,
  Text,
} from '@hopskipdrive/react-components';
import { ArrowDropDown } from '@mui/icons-material';

import { CloseButton, FilterButton, FilterTypeContainer } from '../FilterPanel.styles';

import { CustomListItem, CustomPopper, NoResultsText } from './SlugSearchFilter.styles';
import { FilteredOption, useSlugSearch } from './useSlugSearch';

interface SlugSearchFilterProps {
  filterName: string;
}

export const SlugSearchFilter: FC<SlugSearchFilterProps> = ({ filterName }) => {
  const [search, setSearch] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState(null);
  const { filteredOptions, searchBySlug, isFetching, isLoading, isSuccess, fetchNextPage } = useSlugSearch(filterName);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOptions, setSelectedOptions] = useState<FilteredOption[]>([]);

  const paramIdentifier = paramMapper[filterName];

  const [params, setParams] = useState(Object.fromEntries(searchParams.entries()));
  const hasType = params[paramIdentifier] && params[paramIdentifier].length > 0;

  useEffect(() => {
    setParams(Object.fromEntries(searchParams.entries()));

    const selectedItemsString = searchParams.get(paramIdentifier);

    if (selectedItemsString) {
      setSelectedOptions(JSON.parse(decodeURIComponent(selectedItemsString)));
    } else {
      setSelectedOptions([]);
    }
  }, [searchParams]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setSearch('');
    searchBySlug('');
    setAnchorEl(null);
  };

  const handleOptionClick = (_, newValue: FilteredOption[]) => {
    const newParams = new URLSearchParams(searchParams);

    if (searchParams.get('page')) {
      newParams.set('page', '1');
    }

    newParams.set(paramIdentifier, encodeURIComponent(JSON.stringify(newValue)));
    setSelectedOptions(newValue);
    setSearchParams(newParams);

    if (newValue.length === 0) {
      newParams.delete(paramIdentifier);
      setSearchParams(newParams);
    }

    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? `${filterName}-filter-popover` : undefined;
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <>
      <FilterButton variant="text" open={open} active={Boolean(hasType)} onClick={handleOpen}>
        {capitalize(filterName)}
        <ArrowDropDown />
      </FilterButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{ '.MuiBackdrop-root': { backgroundColor: 'transparent' } }}
      >
        <FilterTypeContainer sx={{ width: '20rem' }}>
          <Box className="filter-header">
            <Text variant="body2">Filter by {capitalize(filterName)}</Text>
            <CloseButton onClick={handleClose}>
              <Svg icon="cross" />
            </CloseButton>
          </Box>
          <PaginatedSearchDropdown
            perPage={10}
            pageNumber={1}
            options={filteredOptions}
            open={true}
            setInputValue={setSearch}
            value={selectedOptions}
            isOptionEqualToValue={(option: FilteredOption, value) =>
              filteredOptions.length === 0 || option.value === value.value
            }
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
            filterOptions={(options) => options}
            inputValue={search}
            onInputChange={(_, value) => {
              searchBySlug(value);
              setSearch(value);
            }}
            renderInput={(props: AutocompleteRenderInputParams) => (
              <FormInputText
                {...props}
                variant="outlined"
                fullWidth
                placeholder="Search by name or slug"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
              />
            )}
            onChange={handleOptionClick}
            PopperComponent={CustomPopper}
            multiple
            renderTags={() => null}
            renderOption={(props, option) => <CustomListItem {...props}>{option.label}</CustomListItem>}
            clearIcon={false}
            isLoading={isLoading}
            loadNextPage={fetchNextPage}
          />
          {!isLoading && !isFetching && !!search && isSuccess && filteredOptions.length === 0 && (
            <NoResultsText variant="body1">No records found</NoResultsText>
          )}
        </FilterTypeContainer>
      </Popover>
    </>
  );
};

const paramMapper = {
  account: 'accountIds',
  rider: 'riderIds',
  careDriver: 'driverIds',
};
